/**
 * The canonical entry point into signup.
 *
 * Landing CTAs used to point at `/quiz`, which named the questionnaire rather
 * than the thing the user is actually starting. Onboarding is the product
 * surface: it welcomes, explains how a dinner works, prices the seat, and
 * only then asks questions. `/quiz` still resolves to the same experience so
 * live ad links keep working, but every new link should use this helper.
 *
 * On localhost the app is served under `/app`; in production the app lives on
 * its own subdomain, so the path has no prefix.
 */
export const ONBOARDING_PATH = "onboarding";

const APP_ORIGIN = "https://app.outclose.com";

function isLocalHost(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * Builds an absolute (or, on localhost, app-relative) onboarding URL.
 *
 * @param params Query params to append. Undefined/null/empty values are
 *   dropped, so callers can pass optional targeting params inline without
 *   building conditional strings at each callsite.
 *
 * Common params:
 *   a  — audience ("m" men / "s" sapphic / "g" general), pins the community
 *        picker for paid traffic
 *   et — ad event type ("c" coffee / "d" dinner)
 *   city — preselects the user's city
 */
export function onboardingHref(
  params: Record<string, string | null | undefined> = {},
): string {
  const base = isLocalHost()
    ? `/app/${ONBOARDING_PATH}`
    : `${APP_ORIGIN}/${ONBOARDING_PATH}`;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, value);
    }
  }

  const query = search.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Builds the onboarding URL for a specific ad creative.
 *
 * Expands the hero slug into the params the rest of the flow already reads,
 * rather than teaching every consumer about `v`:
 *   v  — which hero to render on step 0
 *   a  — pins the community, which also SKIPS the community picker step
 *   et — drives coffee-vs-dinner copy and guided post-signup booking
 *
 * Point ad creatives at this. `slug` must exist in ONBOARDING_HEROES; an
 * unknown slug still produces a working link, just with the generic hero.
 */
export function onboardingHrefForHero(hero: {
  slug: string;
  audience: "men" | "sapphic" | "mixed";
  eventType: "c" | "d";
}): string {
  const audienceParam = { men: "m", sapphic: "s", mixed: "g" }[hero.audience];
  return onboardingHref({
    v: hero.slug,
    a: audienceParam,
    et: hero.eventType,
  });
}

/**
 * True for the routes that render onboarding for a signed-out visitor.
 *
 * MainApp uses this in three places (the sign-in overlay, the native
 * StartPage bypass, and the signed-out render branch); they must agree, or a
 * visitor gets an auth wall slammed over the first screen of the funnel.
 */
export function isOnboardingRoute(location: string): boolean {
  return (
    location.startsWith(`/${ONBOARDING_PATH}`) || location.startsWith("/quiz")
  );
}
